import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    assignModelOptions,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfigService,
    ConfirmationTimeRecordingModel,
    DisplayDecorator,
    EventService,
    isTimeRecordingGeneralEntryModel,
    ModalClass,
    NotificationService,
    ODataModelChangeEvent,
    TextModel,
    TIMERECORDING_TYPE_OF_WORK_CODES,
    TimeRecordingEntryModel,
    TimeRecordingGeneralEntryModel,
    TimeRecordingLimitedEntryModel,
    TimeRecordingService,
    TimeRecordingValidationResultModel,
    TimeRecordingValidationService,
    TimesheetEntryInterface,
    TimesheetModel,
    UiResponsiveService,
    ValueHelperService,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, take, takeUntil } from 'rxjs/operators';
import { faPlus, faTimes, faTrashAlt } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'pace-confirmation-time-recording',
    templateUrl: './confirmation-time-recording.component.html',
    styleUrls: ['./confirmation-time-recording.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationTimeRecordingComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input()
    public model: ConfirmationTimeRecordingModel;

    @Input()
    public parentForm: FormArray;

    public timeRecordingForm: FormGroup;
    public timeRecordingFormChildren: FormArray;

    public allFalsyValidationResults: TimeRecordingValidationResultModel[] = [];
    public falsyValidationResultsCommon: TimeRecordingValidationResultModel[] = [];
    public otherTimeSheetEntries: TimesheetEntryInterface[] = [];

    public displayNewEntryForm: boolean;
    public displayBillableDropdowns: boolean;
    public displayActivities: boolean;
    public displayAdditionalTravelInfo: boolean;
    public displayMeansOfTransport: boolean;
    public displayOtherTimeRecordingEntries: boolean;
    public enableAfterHourCallOut: boolean;
    public enableStandbyTravel = false;
    public faTrashAlt = faTrashAlt;
    public faPlus = faPlus;
    public faTimes = faTimes;
    public clickedTimeRecEntryIndex: number | undefined = undefined;
    public computedWarnings: { [key: string]: { [key: string]: boolean } } = {};
    public confirmationTimeRecordingDestroy = new Subject<void>();
    public isMobile$: Observable<boolean>;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly formBuilder: FormBuilder,
        private readonly modalService: BsModalService,
        private readonly timeRecordingService: TimeRecordingService,
        private readonly timeRecordingValidationService: TimeRecordingValidationService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly eventService: EventService,
        private readonly configService: ConfigService,
        private readonly valueHelperService: ValueHelperService,
        private readonly notificationService: NotificationService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly uiResponsiveService: UiResponsiveService,
    ) {
        this.displayNewEntryForm = false;
    }

    public ngOnInit(): void {
        this.isMobile$ = this.uiResponsiveService.isMobile$;
        this.displayBillableDropdowns = this.clientManager.isNgmP58();
        this.displayActivities = this.clientManager.isNgmP58();
        this.displayAdditionalTravelInfo = this.configService.getDisplayAdditionalTravelInfo();
        this.displayMeansOfTransport = this.configService.getDisplayMeansOfTransport();
        this.enableStandbyTravel = this.configService.getEnableStandbyTravel();
        this.displayOtherTimeRecordingEntries = this.configService.getAllowNotificationPause();
        this.enableAfterHourCallOut = this.configService.getEnabledAfterHoursCallOut();
        const timeRecordingValidator = (): { [p: string]: unknown } | null => this.checkValidity();
        this.timeRecordingFormChildren = this.formBuilder.array([]);
        this.timeRecordingForm = this.formBuilder.group(
            {
                children: this.timeRecordingFormChildren,
            },
            {
                validators: timeRecordingValidator,
            },
        );

        this.parentForm.push(this.timeRecordingForm);
        const otherEntries = [...this.model.otherTimeRecordingEntries, ...this.model.timesheetEntries];
        this.otherTimeSheetEntries = TimesheetModel.sortTimesheetEntries(otherEntries);

        this.triggerTimeRecordingBillableOptionChanged();

        this.uiResponsiveService.currentScreenSize$
            .pipe(takeUntil(this.confirmationTimeRecordingDestroy))
            .subscribe(() => {
                this.clickedTimeRecEntryIndex = undefined;
                this.displayNewEntryForm = false;
                this.changeDetectorRef.detectChanges();
            });
    }

    public ngAfterViewInit(): void {
        this.timeRecordingForm.updateValueAndValidity();

        if (this.displayBillableDropdowns) {
            this.enableBillablePresetDropdown();
            if (!this.model.isNotificationRemoteWork) {
                const firstEntry = this.timeRecordingService.getFirstTimeRecordingEntry(
                    this.model.timeRecordingEntries.value,
                );
                if (firstEntry && this.isGeneralEntry(firstEntry) && firstEntry.isPhoneFix()) {
                    firstEntry.disableBillableDropdown();
                    this.disableBillablePresetDropdown();
                }
            }
        }
    }

    public ngOnDestroy(): void {
        this.confirmationTimeRecordingDestroy.next();
        this.confirmationTimeRecordingDestroy.complete();
    }

    public disableAddNewEntryForm(): boolean {
        return this.model.timeRecordingEntries.value.some(
            (entry) => !entry.endTimeModel.value.isValid() && isTimeRecordingGeneralEntryModel(entry),
        );
    }

    public hasAnyOSREntry(): boolean {
        return (
            this.model?.timeRecordingEntries?.value?.some((entry: TimeRecordingEntryModel) => entry.isOSREntry()) ??
            false
        );
    }

    private checkValidity<T>(): null | { [keys: string]: T } {
        const errors: { [keys: string]: T } = {};

        const validationResult = this.timeRecordingValidationService.performValidation(
            this.model.notification.identifier,
            this.model.timeRecordingEntries.value,
            this.model.otherTimeRecordingEntries,
            this.model.isNotificationRemoteWork,
        );
        this.allFalsyValidationResults = validationResult.filter((validationEntry) => !validationEntry.isValid);
        this.falsyValidationResultsCommon = this.allFalsyValidationResults.filter(
            (validationEntry) => !validationEntry.showAtId,
        );

        if (this.allFalsyValidationResults.length > 0) {
            errors[this.allFalsyValidationResults[0].validationErrorKey] = this.translate.instant(
                this.allFalsyValidationResults[0].validationErrorMessage,
            );
        }
        if (this.displayNewEntryForm) {
            this.eventService.triggerTimeRecordingValidationChanged();
        }

        this.computeAllEntryWarnings();

        return Object.keys(errors).length > 0 ? errors : null;
    }

    private computeAllEntryWarnings(): void {
        this.computedWarnings = {};
        this.model.timeRecordingEntries.value.forEach((entry: TimeRecordingEntryModel) => {
            this.computedWarnings[entry.id] = this.getEntryWarnings(entry);
        });
    }

    private getEntryWarnings(entry: TimeRecordingEntryModel): { [key: string]: boolean } {
        const warnings = {
            isEntryDurationEqualOrExceeding24Hours: this.isEntryDurationEqualOrExceeding24Hours(entry),
            isGeneralEntryDurationExceeding10Hours: this.isGeneralEntryDurationExceeding10Hours(entry),
            isOvernightEntry: this.isOvernightEntry(entry),
            isEntryStartingWithAGapOf10HoursOrMore: this.isEntryStartingWithAGapOf10HoursOrMore(entry),
            isEntriesHavingAnUnoccupiedGap: this.isEntriesHavingAnUnoccupiedGap(entry),
        };

        return {
            ...warnings,
            hasAnyWarnings: warnings && Object.values(warnings).some((warning) => warning),
        };
    }

    /**
     * This function is to update the Phone fix / Remote work dependencies
     * This function is called onTypeWorkChange and also when new entry is added
     * Time of Recovery should be updated only if new entry added but not on Type of work change
     *
     * @param isNewEntry - true if it's a new entry
     * @private
     */
    private updatePhoneFixDependency(isNewEntry = false): void {
        // It is not allowed to change the billing code in case of "phone fix"
        if (!this.model.isNotificationRemoteWork) {
            this.disableBillablePresetDropdown();
        }
        this.triggerTimeRecordingBillableOptionChanged();
        if (isNewEntry) {
            this.updateTimeOfRecoveryMaxValue();
        }
    }

    public insertEntry(newEntry: TimeRecordingGeneralEntryModel): void {
        if (this.model.isNotificationRemoteWork) {
            this.timeRecordingService.insertEntry(this.model.timeRecordingEntries.value, newEntry);
            this.updatePhoneFixDependency(true);
            this.updateTimesheetStartOfWork(newEntry);
        } else if (newEntry.isPhoneFix()) {
            this.showPhoneFixModal().subscribe((result) => {
                if (result) {
                    // call actions in timeRecordingService to remove all other time recording entries, activites and travel rows
                    this.model.clearTimeRecording();
                    this.model.addPhoneFixEntry(newEntry.getTypeOfWorkCode());
                    this.timeRecordingForm.updateValueAndValidity();
                    this.hideNewEntryForm();
                    this.changeDetectorRef.markForCheck();
                    this.updatePhoneFixDependency(true);
                }
            });
        } else {
            this.timeRecordingService.insertEntry(this.model.timeRecordingEntries.value, newEntry);
            this.timeRecordingForm.updateValueAndValidity();
            this.hideNewEntryForm();
            this.updateTimeOfRecoveryMaxValue();
        }
        this.triggerTimeRecordingBillableOptionChanged();
    }

    public deleteEntry(index: number): void {
        this.timeRecordingService.deleteEntry(this.model, index);
        this.triggerTimeRecordingBillableOptionChanged();
        this.timeRecordingForm.updateValueAndValidity();
        this.updateTimeOfRecoveryMaxValue();
    }

    // @TODO refactor method to encapsulate the business login in a pure function
    public onTypeOfWorkChange(
        changedEntry: ODataModelChangeEvent<DisplayDecorator<TextModel>>,
        timeRecEntry: TimeRecordingEntryModel,
    ): void {
        if (!this.isGeneralEntry(timeRecEntry)) {
            return;
        }
        const oldValue = (<DisplayDecorator<TextModel>>(<unknown>changedEntry.oldValue))?.model.value;
        const wasPhoneFixSelected =
            oldValue &&
            (oldValue === TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX ||
                oldValue === TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX_EXPENSES);
        if (this.model.isNotificationRemoteWork) {
            this.updatePhoneFixDependency();
        } else if (!wasPhoneFixSelected && timeRecEntry.isPhoneFix()) {
            this.updatePhoneFixModel(timeRecEntry, oldValue);
        } else {
            // We need to check if the dropdown has been switched from phone-fix to non phone-fix general entry again
            // if the old value was "phone fix" (PFI), all other rows had been removed. We need to re-add them
            if (wasPhoneFixSelected && !timeRecEntry.isPhoneFix()) {
                if (this.clientManager.isNgmP58OrSA()) {
                    this.model.resetTimeRecording();
                }
                // make the preset dropdown be changeable again as might have been disabled by the "phone fix process"
                this.enableBillablePresetDropdown();
            }
        }
        // since the model is changed in a way that new entries are added to it, and form has not been updated with the added controls yet
        // so a change detection needs to be run for updating the form's validity correctly.
        this.changeDetectorRef.detectChanges();
        this.updateTimeOfRecoveryMaxValue();
    }

    public changeDurationForSingleEntry(changedTimeRecEntry: TimeRecordingEntryModel): void {
        this.timeRecordingService.changeDurationForSingleEntry(
            this.model.notification.identifier,
            this.model.timeRecordingEntries.value,
            changedTimeRecEntry,
            this.model.isNotificationRemoteWork,
        );
        this.updateTimesheetStartOfWork(changedTimeRecEntry);
        this.timeRecordingForm.updateValueAndValidity();
        this.updateTimeOfRecoveryMaxValue();
    }

    public changeStartTimeForSubsequentEntries(changedTimeRecEntry: TimeRecordingEntryModel): void {
        if (changedTimeRecEntry.endTimeModel.value.isValid()) {
            changedTimeRecEntry.setEndDateTime(changedTimeRecEntry.endDateModel.value);
        }
        this.timeRecordingService.changeStartTimeForSubsequentEntries(
            this.model.timeRecordingEntries.value,
            changedTimeRecEntry,
        );
        this.timeRecordingForm.updateValueAndValidity();
        this.updateTimeOfRecoveryMaxValue();
    }

    public onBillablePresetChange(): void {
        this.timeRecordingService.setBillableCodeForAllEntries(
            this.model.timeRecordingEntries.value,
            this.model.billablePresetModel.selectedEntry.model.value,
        );
        this.triggerTimeRecordingBillableOptionChanged();
    }

    public onBillableChange(): void {
        // raise event for equipment screen commercial info desc
        this.triggerTimeRecordingBillableOptionChanged();
        this.timeRecordingForm.updateValueAndValidity();
    }

    private isCommercialInfoRequired(): boolean {
        return this.configService.getCommercialInfMandatoryForNonBill()
            ? this.isTimeRecordingEntriesNonBillable()
            : this.isTimeRecordingEntriesBillableOrFoc();
    }

    private triggerTimeRecordingBillableOptionChanged(): void {
        const isCommercialInfoMandatory = this.isCommercialInfoRequired();
        this.eventService.triggerTimeRecordingBillableOptionChanged(isCommercialInfoMandatory);
    }

    public showPhoneFixModal(): Observable<boolean> {
        const modalInstance = this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new YesNoModalDialogModel(
                    <string>this.translate.instant('CONFIRMATION_TIMERECORDING_CONFIRM_PHONEFIX_TITLE'),
                    this.clientManager.isNgmSA()
                        ? <string>this.translate.instant('CONFIRMATION_TIMERECORDING_CONFIRM_REMOTE_SUPPORT_MESSAGE')
                        : <string>this.translate.instant('CONFIRMATION_TIMERECORDING_CONFIRM_PHONEFIX_MESSAGE'),
                    <string>this.translate.instant('CONFIRMATION_TIMERECORDING_CONFIRM_PHONEFIX_YES'),
                    <string>this.translate.instant('CONFIRMATION_TIMERECORDING_CONFIRM_PHONEFIX_NO'),
                ),
            },
            ariaLabelledBy: 'yes-no-modal-title',
        });

        return modalInstance.content?.modalResult ?? of(false);
    }

    public resetTimeRecording(): void {
        const modalInstance = this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new YesNoModalDialogModel(
                    <string>this.translate.instant('CONFIRMATION_TIMERECORDING_RESET_TITLE'),
                    <string>this.translate.instant('CONFIRMATION_TIMERECORDING_RESET_MESSAGE'),
                    <string>this.translate.instant('CONFIRMATION_TIMERECORDING_RESET_YES'),
                    <string>this.translate.instant('CONFIRMATION_TIMERECORDING_RESET_NO'),
                ),
            },
        });

        modalInstance.content?.modalResult.subscribe((modalResult: boolean) => {
            if (modalResult) {
                this.model.resetTimeRecording();
                this.enableBillablePresetDropdown();
                // raise event for equipment screen commercial info desc
                this.triggerTimeRecordingBillableOptionChanged();
                this.timeRecordingForm.updateValueAndValidity();
            }
            // need to add this to the changeDetector, for the view to get updated, because we are on a subscribe, in a modal.
            this.changeDetectorRef.markForCheck();
            this.updateTimeOfRecoveryMaxValue();
        });
    }

    public toggleShowNewEntryForm(): void {
        this.displayNewEntryForm = !this.displayNewEntryForm;
        this.changeDetectorRef.detectChanges();
    }

    public displayIndexedAddNewEntryForm(index: number): void {
        if (this.clickedTimeRecEntryIndex === index) {
            this.clickedTimeRecEntryIndex = undefined;
        } else {
            this.clickedTimeRecEntryIndex = index;
        }
    }

    public hideNewEntryForm(): void {
        this.displayNewEntryForm = false;
        this.clickedTimeRecEntryIndex = undefined;
        this.changeDetectorRef.detectChanges();
    }

    private isEntryDurationEqualOrExceeding24Hours(timeRecEntry: TimeRecordingEntryModel): boolean {
        return this.timeRecordingValidationService.checkDurationEqualorExceeding24Hours(timeRecEntry);
    }

    private isGeneralEntryDurationExceeding10Hours(timeRecEntry: TimeRecordingEntryModel): boolean {
        const hours = 10;

        return (
            this.clientManager.isNgmP58() &&
            this.timeRecordingValidationService.checkDurationEqualOrExceedingHours(timeRecEntry, hours)
        );
    }

    private isOvernightEntry(timeRecEntry: TimeRecordingEntryModel): boolean {
        return this.clientManager.isNgmP58() && timeRecEntry.isWorkEntry() && timeRecEntry.isOverNightEntry();
    }

    private isEntryStartingWithAGapOf10HoursOrMore(timeRecEntry: TimeRecordingEntryModel): boolean {
        return (
            this.model.timeRecordingEntries.value.length > 1 &&
            this.timeRecordingValidationService.isEntryStartingWithAGapOf10HoursOrMore(
                this.model.timeRecordingEntries.value,
                timeRecEntry,
            )
        );
    }

    private isEntriesHavingAnUnoccupiedGap(timeRecEntry: TimeRecordingEntryModel): boolean {
        if (this.configService.getAllowNotificationPause() && this.model.timeRecordingEntries.value.length > 1) {
            return this.timeRecordingValidationService.isEntriesHavingAnUnoccupiedGap(
                this.otherTimeSheetEntries,
                this.model.timeRecordingEntries.value,
                timeRecEntry,
            );
        }
        return false;
    }

    public shouldShowAddEntryButton(): boolean {
        return (
            !this.displayNewEntryForm &&
            this.timeRecordingService.getEntriesByMultipleTypes(
                [TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX, TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX_EXPENSES],
                this.model.timeRecordingEntries.value,
            ).length === 0
        );
    }

    public shouldShowDeleteEntryButton(timeRecEntry: TimeRecordingEntryModel): boolean {
        // If standby travel is enabled, it is possible to add or delete an additional travel-to entry in time-recording screen.
        // So, we need to check the count of travel-to and OSR entries separately.
        // Which means, if there are more than 1 travel-to entries, we need to show the delete button for travel-to entries.
        // And, if there are more than 1 osr entries, we need to show the delete button for osr entries.
        if (this.enableStandbyTravel) {
            return timeRecEntry.isTravelToEntry()
                ? this.timeRecordingService.getTravelToEntries(this.model.timeRecordingEntries.value).length > 1
                : this.isGeneralEntry(timeRecEntry) &&
                      this.timeRecordingService.getNonTravEntries(this.model.timeRecordingEntries.value).length > 1;
        }
        // For P58, If current status is Remote work, then Minimum time recording entries is 1,
        // Lets say user adds new entry, so out of two entries user can delete any 1 entry
        if (this.notificationService.isNotificationRemoteWork(this.model.notification)) {
            const minTimeRecordingEntriesForRemoteWork = 1;
            return (
                this.isGeneralEntry(timeRecEntry) &&
                this.model.timeRecordingEntries.value.length > minTimeRecordingEntriesForRemoteWork
            );
        }
        // Otherwise, it is not possible to delete TRAV or PHONE_FIX entries
        // there must always be at least three row in the time-recording.
        // If a 4th or more is present its allowed to delete general time-recording entries
        const minTimeRecordingEntries = 3;
        return (
            this.isGeneralEntry(timeRecEntry) &&
            !timeRecEntry.isPhoneFix() &&
            this.model.timeRecordingEntries.value.length > minTimeRecordingEntries
        );
    }

    public shouldShowActivityDropdown(timeRecEntry: TimeRecordingEntryModel): boolean {
        return this.isGeneralEntry(timeRecEntry) && timeRecEntry.isActivityRequired();
    }

    public isGeneralEntry(timeRecEntry: TimeRecordingEntryModel): timeRecEntry is TimeRecordingGeneralEntryModel {
        return timeRecEntry instanceof TimeRecordingGeneralEntryModel;
    }

    public isLimitedEntry(timeRecEntry: TimeRecordingEntryModel): timeRecEntry is TimeRecordingLimitedEntryModel {
        return timeRecEntry instanceof TimeRecordingLimitedEntryModel;
    }

    private disableBillablePresetDropdown(): void {
        this.model.billablePresetModel = assignModelOptions(
            this.model.billablePresetModel,
            {
                ...this.model.billablePresetModel.options,
                disabled: true,
            },
            this.model,
        );
        this.changeDetectorRef.detectChanges();
    }

    private enableBillablePresetDropdown(): void {
        this.model.billablePresetModel = assignModelOptions(
            this.model.billablePresetModel,
            {
                ...this.model.billablePresetModel.options,
                disabled: false,
            },
            this.model,
        );
        this.changeDetectorRef.detectChanges();
    }

    public shouldShowDestinationDropdown(timeRecEntry: TimeRecordingEntryModel): boolean {
        return (
            this.clientManager.isNgmAAIndia() &&
            timeRecEntry.getTypeOfWorkCode() === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK
        );
    }

    /**
     * Check if Billable dropdown of any Time recording entry is selected as BILL or FOC
     *
     * Remarks:
     * For P5 additional FOC options were introduced (L01, L02, L03, ...). These have to return true as well.
     *
     * @returns {boolean} true if any entry is selected as BILL or FOC
     */
    private isTimeRecordingEntriesBillableOrFoc(): boolean {
        return this.model.timeRecordingEntries.value.some((selectedEntry) => {
            const castedEntry = <TimeRecordingGeneralEntryModel | TimeRecordingLimitedEntryModel>selectedEntry;
            return (
                castedEntry.billableModel.selectedEntry &&
                (castedEntry.billableModel.selectedEntry.value === <string>ConfigConstants.BILLABLE_CODE.BILLABLE ||
                    castedEntry.billableModel.selectedEntry?.value === <string>ConfigConstants.BILLABLE_CODE.COURTESY ||
                    this.valueHelperService.codeListForId[
                        ConfigConstants.valueHelperIdFocAdditionalLaborCodes
                    ]?.includes(castedEntry.billableModel.selectedEntry.value))
            );
        });
    }

    /**
     * Check if Billable dropdown of any Time recording entry is selected as NON BILLABLE
     *
     * @returns {boolean} true if any entry is selected as NON BILLABLE
     */
    private isTimeRecordingEntriesNonBillable(): boolean {
        return this.model.timeRecordingEntries.value.some((selectedEntry) => {
            const entry = <TimeRecordingGeneralEntryModel | TimeRecordingLimitedEntryModel>selectedEntry;
            return (
                entry.billableModel.selectedEntry &&
                entry.billableModel.selectedEntry.value === <string>ConfigConstants.BILLABLE_CODE.NON_BILLABLE
            );
        });
    }

    public updateTimeOfRecoveryMaxValue(): void {
        const maxValue = this.timeRecordingService.getMaximumValueForTimeOfRecovery(
            this.model.timeRecordingEntries.value,
            this.model.isNotificationRemoteWork,
        );
        this.eventService.triggerUpdateTimeOfRecoveryMaxValue(maxValue);
    }

    // The 'Add travel to' button should be displayed only if standby travel is enabled and there is only 1 travel-to entry.
    public displayAdditionalTravelToButton(timeRecEntry: TimeRecordingEntryModel): boolean {
        return (
            this.enableStandbyTravel &&
            timeRecEntry.isTravelToEntry() &&
            this.timeRecordingService.getTravelToEntries(this.model.timeRecordingEntries.value).length === 1
        );
    }

    public addTravelToEntry(timeRecEntry: TimeRecordingEntryModel): void {
        // Only one additional travel entry can be added
        if (this.timeRecordingService.getTravelToEntries(this.model.timeRecordingEntries.value).length > 1) {
            this.log.warn(
                'ConfirmationTimeRecordingComponent: addTravelToEntry: Cannot add additional travel-to entry as there are already more than one travel-to entries',
            );
            return;
        }
        // Based on the typeOfWork of the existing travel-to entry, the new travel-to entry's typeOfWork is decided
        const typeOfTravel =
            timeRecEntry.getTypeOfWorkCode() === TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_TO_STANDBY
                ? TIMERECORDING_TYPE_OF_WORK_CODES.TRTO
                : TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_TO_STANDBY;
        const travEntry = this.model.createNewLimitedTimeRecordingModel(
            timeRecEntry.endDateTime.clone(),
            timeRecEntry.endDateTime.clone(),
            typeOfTravel,
            false,
        );
        this.timeRecordingService.insertEntry(this.model.timeRecordingEntries.value, travEntry);
    }

    private updateTimesheetStartOfWork(changedTimeRecEntry: TimeRecordingEntryModel): void {
        if (!this.clientManager.isNgmP58()) {
            return;
        }
        this.timeRecordingService
            .updateTimesheetStartOfWork(
                this.model.notification.identifier,
                this.model.timeRecordingEntries.value,
                changedTimeRecEntry,
            )
            .pipe(take(1))
            .subscribe();
    }

    private updatePhoneFixModel(timeRecEntry: TimeRecordingGeneralEntryModel, oldValue: string): void {
        this.showPhoneFixModal()
            .pipe(
                map((result) => {
                    if (result) {
                        if (this.clientManager.isNgmP58OrSA()) {
                            // remove all other time recording entries, activities and travel rows
                            this.model.clearTimeRecording();
                        }
                        if (this.clientManager.isNgmAA()) {
                            // remove only general(OSR/Break/Wait etc.) time recording entries
                            this.model.clearTimeRecordingGeneralEntries();
                        }
                        this.model.addPhoneFixEntry(timeRecEntry.getTypeOfWorkCode());
                        this.updatePhoneFixDependency();
                    } else {
                        timeRecEntry.typeOfWorkModel.selectByValue(oldValue);
                    }
                }),
                catchError(() => {
                    timeRecEntry.typeOfWorkModel.selectByValue(oldValue);
                    return of(undefined);
                }),
            )
            .subscribe(() => {
                this.hideNewEntryForm();
                this.timeRecordingForm.updateValueAndValidity();
                // need to add this to the changeDetector, for the view to get updated, because we are on a subscribe, in a modal.
                this.changeDetectorRef.markForCheck();
                this.updateTimeOfRecoveryMaxValue();
            });
    }
}
